import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { wholeDollars } from '../../lib/format.js'
import { Icon } from '../Icon.js'
import { Badge } from '../ui.js'
import { serviceName, shows, type AgreementGroup, type AgreementRow, type GroupBy } from './agreement-view.js'

export type Ticked = boolean | 'mixed'

/** A native checkbox, so a group that is partly ticked can say so. */
export function TickBox({
  checked,
  onChange,
  label,
  className,
}: {
  checked: Ticked
  onChange: (checked: boolean) => void
  label: string
  className?: string
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = checked === 'mixed'
  }, [checked])
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      checked={checked === true}
      onChange={() => onChange(checked !== true)}
      className={cn('size-4 shrink-0 cursor-pointer accent-brand-600', className)}
    />
  )
}

/** How much of `ids` is in `selected`. */
export function tickedOf(ids: readonly string[], selected: ReadonlySet<string>): Ticked {
  const on = ids.filter((id) => selected.has(id)).length
  return on === 0 ? false : on === ids.length ? true : 'mixed'
}

/**
 * A value just saved, drawn until the server's copy changes or the save is
 * given up. `hold` it on the way out and `release` it when the save fails.
 */
export function useHeld<T>(value: T): [T, (next: T) => void, () => void] {
  const [held, setHeld] = useState<{ value: T } | null>(null)
  const [seen, setSeen] = useState(value)
  if (!Object.is(seen, value)) {
    setSeen(value)
    setHeld(null)
  }
  return [held ? held.value : value, (next) => setHeld({ value: next }), () => setHeld(null)]
}

/**
 * Text that becomes a box when it is pressed. Enter or leaving it saves,
 * Escape puts it back. An empty box saves an empty string.
 */
export function InlineText({
  display,
  value,
  placeholder,
  label,
  onSave,
  className,
}: {
  display: ReactNode
  value: string
  placeholder?: string
  label: string
  onSave: (next: string) => Promise<unknown>
  className?: string
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const [shown, hold, release] = useHeld(value)
  const cancelled = useRef(false)

  if (draft === null) {
    return (
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => {
          cancelled.current = false
          setDraft(shown)
        }}
        className={cn('group/inline -mx-1 inline-flex min-w-0 items-baseline gap-1 rounded px-1 text-left hover:bg-ink-100', className)}
      >
        <span className="min-w-0 truncate">{shown === value ? display : shown || placeholder}</span>
        <Icon name="pencil" className="size-3 shrink-0 self-center text-ink-400 opacity-0 group-hover/inline:opacity-100 group-focus-visible/inline:opacity-100" />
      </button>
    )
  }

  const commit = () => {
    const next = draft.trim()
    setDraft(null)
    if (cancelled.current || next === shown) return
    hold(next)
    onSave(next).catch(release)
  }
  return (
    <input
      autoFocus
      aria-label={label}
      value={draft}
      placeholder={placeholder}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur()
        if (event.key === 'Escape') {
          cancelled.current = true
          setDraft(null)
        }
      }}
      className={cn(
        'min-w-[200px] rounded-md bg-white px-1.5 py-0.5 ring-1 ring-brand-400 outline-none ring-inset',
        className,
      )}
    />
  )
}

/**
 * A site's or a service's heading, in the timeline and the table alike:
 * the fold, the group's tick, its name and what is under it, its actions and
 * what it is worth a year.
 *
 * Folded, it is one line in the first column: the name opens it, then the
 * services at the site (or how many sites have the service) and what needs
 * seeing to. Its actions and its worth wait until it is open.
 */
export function GroupHeading({
  group,
  by,
  open = true,
  onFold,
  ticked,
  onTick,
  onRename,
  onEditSite,
  onAddService,
  onAddSites,
}: {
  group: AgreementGroup
  by: GroupBy
  /** Absent `onFold`, the group cannot fold and is always open. */
  open?: boolean
  onFold?: () => void
  ticked?: Ticked
  onTick?: (checked: boolean) => void
  /** By site: the site's own name, which an empty box clears. */
  onRename?: (name: string | null) => Promise<unknown>
  onEditSite?: () => void
  onAddService?: () => void
  onAddSites?: () => void
}) {
  const tick = onTick ? (
    <TickBox
      checked={ticked ?? false}
      onChange={onTick}
      label={`Select every service ${by === 'site' ? 'at' : 'of'} ${group.name}`}
      className="self-center"
    />
  ) : null

  if (!open && onFold) {
    // One line in a narrow column: the name gets the room, then what is
    // there and what needs doing.
    const summary = by === 'site' ? [...new Set(group.rows.map(serviceName))].join(', ') : group.sub
    return (
      <span className="flex w-full min-w-0 items-center gap-1.5">
        <button
          type="button"
          onClick={onFold}
          aria-expanded={false}
          aria-label={`Open ${group.name}`}
          className="grid size-5 shrink-0 place-items-center rounded text-ink-500 hover:bg-ink-100"
        >
          <Icon name="forward" className="size-3.5" />
        </button>
        {tick}
        <button
          type="button"
          onClick={onFold}
          tabIndex={-1}
          title={[group.name, by === 'site' ? group.sub : null, summary].filter(Boolean).join(' · ')}
          className="max-w-[70%] min-w-0 shrink-0 truncate rounded text-left text-[13px] font-semibold text-ink-900 hover:text-brand-700"
        >
          {group.name}
        </button>
        {summary ? <span className="min-w-0 flex-1 truncate text-xs text-ink-400">{summary}</span> : null}
        <span className="ml-auto flex shrink-0 items-center gap-1">
          <GroupFlags rows={group.rows} />
        </span>
      </span>
    )
  }

  const name =
    by === 'site' && onRename ? (
      <InlineText
        display={group.name}
        value={group.siteName ?? ''}
        placeholder={group.name}
        label={`Rename ${group.name}`}
        onSave={(next) => onRename(next || null)}
        className="text-[13.5px] font-semibold text-ink-900"
      />
    ) : (
      <span className="text-[13.5px] font-semibold text-ink-900">{group.name}</span>
    )

  return (
    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      {onFold ? (
        <button
          type="button"
          onClick={onFold}
          aria-expanded
          aria-label={`Fold ${group.name}`}
          className="-mr-1 grid size-5 place-items-center self-center rounded text-ink-500 hover:bg-ink-100"
        >
          <Icon name="chevronDown" className="size-3.5" />
        </button>
      ) : null}
      {tick}
      {name}
      {group.sub ? <span className="text-xs text-ink-500">{group.sub}</span> : null}
      <span className="ml-auto flex items-baseline gap-1.5">
        {onEditSite ? (
          <HeadingAction icon="pencil" label={`Edit ${group.name}`} onClick={onEditSite}>
            Edit site
          </HeadingAction>
        ) : null}
        {onAddService ? (
          <HeadingAction icon="plus" label={`Add a service at ${group.name}`} onClick={onAddService} brand>
            Add service
          </HeadingAction>
        ) : null}
        {onAddSites ? (
          <HeadingAction icon="plus" label={`Add sites to ${group.name}`} onClick={onAddSites} brand>
            Add sites
          </HeadingAction>
        ) : null}
        <span className="ml-1.5 shrink-0 text-xs text-ink-400 tabular-nums">{`${wholeDollars(group.aYear)} a year`}</span>
      </span>
    </span>
  )
}

/** What a folded group asks for, in the chips' own words and tones. */
function GroupFlags({ rows }: { rows: readonly AgreementRow[] }) {
  const overdue = rows.some((row) => shows(row, 'overdue'))
  const job = rows.some((row) => shows(row, 'job'))
  const noPrice = rows.some((row) => shows(row, 'noprice'))
  const paused = rows.every((row) => shows(row, 'paused'))
  return (
    <>
      {overdue ? <Badge tone="red">Overdue</Badge> : null}
      {job ? <Badge tone="blue">Needs a job</Badge> : null}
      {noPrice ? <Badge tone="amber">No price</Badge> : null}
      {paused ? <Badge>Paused</Badge> : null}
    </>
  )
}

/** A service's own flags, on its line under an open group. Its price, or "no price", is already on the line. */
export function RowFlags({ row }: { row: AgreementRow }) {
  const overdue = shows(row, 'overdue')
  const job = shows(row, 'job')
  if (!overdue && !job) return null
  return (
    <>
      {overdue ? <Badge tone="red">Overdue</Badge> : null}
      {job ? <Badge tone="blue">Needs a job</Badge> : null}
    </>
  )
}

function HeadingAction({
  icon,
  label,
  onClick,
  brand = false,
  children,
}: {
  icon: 'pencil' | 'plus'
  label: string
  onClick: () => void
  brand?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium',
        brand ? 'text-brand-700 hover:bg-brand-50' : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900',
      )}
    >
      <Icon name={icon} className="size-3.5" />
      {children}
    </button>
  )
}
